import { ChangeDetectionStrategy, Component, DestroyRef, Inject, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CallState } from 'src/app/models/call';
import { memberName } from 'src/app/models/chat';
import { CallService } from 'src/app/services/call.service';
import { I18nService } from 'src/app/services/i18n.service';

const DEFAULT_AVATAR = 'assets/userAvatar.svg';

// Écran d'appel audio/vidéo, affiché par-dessus toutes les pages (déclaré dans app.component.html) :
// appel entrant (décrocher / refuser), appel sortant qui sonne, puis communication en cours.
@Component({
  selector: 'app-call',
  templateUrl: './call.component.html',
  styleUrls: ['./call.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CallComponent implements OnInit, OnDestroy {

  call: CallState | null = null;
  duration = '';
  readonly name = memberName;
  private durationTimer: ReturnType<typeof setInterval> | null = null;
  private destroyRef = inject(DestroyRef);

  constructor(
    private callService: CallService,
    private snackBar: MatSnackBar,
    private i18n: I18nService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.callService.state$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(call => {
      this.call = call;
      this.updateDurationTimer();
    });
    this.callService.notices$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(notice =>
      this.snackBar.open(this.i18n.t(notice.key, notice.params), this.i18n.t('common.close'), { duration: 5000 }));
  }

  ngOnDestroy(): void {
    this.stopDurationTimer();
  }

  get avatar(): string {
    return this.call?.peer.avatar || DEFAULT_AVATAR;
  }

  // Vidéo distante affichée en grand une fois reçue ; sinon avatar au centre
  get showRemoteVideo(): boolean {
    return !!this.call?.video && !!this.call.remoteStream && this.call.status === 'connected';
  }

  get statusText(): string {
    const call = this.call;
    if (!call) {
      return '';
    }
    switch (call.status) {
      case 'incoming':
        return this.i18n.t(call.video ? 'call.incomingVideo' : 'call.incomingAudio');
      case 'outgoing':
        return this.i18n.t('call.calling');
      case 'connecting':
        return this.i18n.t('call.connecting');
      default:
        return this.duration;
    }
  }

  accept(): void {
    this.callService.accept();
  }

  decline(): void {
    this.callService.decline();
  }

  hangUp(): void {
    this.callService.hangUp();
  }

  toggleMute(): void {
    this.callService.toggleMute();
  }

  toggleCamera(): void {
    this.callService.toggleCamera();
  }

  private updateDurationTimer(): void {
    if (this.call?.startedAt) {
      this.updateDuration();
      if (!this.durationTimer) {
        this.durationTimer = setInterval(() => this.updateDuration(), 1000);
      }
    } else {
      this.stopDurationTimer();
      this.duration = '';
    }
  }

  private updateDuration(): void {
    const seconds = Math.max(0, Math.floor((Date.now() - (this.call?.startedAt || Date.now())) / 1000));
    const pad = (value: number) => String(value).padStart(2, '0');
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    this.duration = (hours ? hours + ':' + pad(minutes) : String(minutes)) + ':' + pad(seconds % 60);
  }

  private stopDurationTimer(): void {
    if (this.durationTimer) {
      clearInterval(this.durationTimer);
      this.durationTimer = null;
    }
  }
}
